import React, { useEffect, useMemo, useState } from 'react';
import {
  SortingState,
  Updater,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  CellContext,
} from '@tanstack/react-table';
import { FunctionData } from '@hud/portal-dtos/webapp-function.dto';
import TableContainer from '@hud/web-components/Container/TableContainer';
import Pagination from '@hud/web-components/Pagination/Pagination';
import { Search } from '@hud/web-components/Search/Search';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { renderDatetime, formatDatetime } from '@hud/web-components/Table/CellRenderers';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import {
  entitiesCellRenderer,
  entitiesCellFormatter,
} from '../entitiesCellRenderer/entitiesCellRenderer';
import renderFunctionMetric, {
  formatFunctionMetric,
} from '../functionMetricCellRenderer/functionMetricCellRenderer';
import renderMetricTrend, {
  formatMetricTrend,
} from '../metricTrendRenderer/metricTrendRenderer';
import Menu from '@hud/web-components/Menu/Menu';
import { FlagFilter, MultiSelectFilter } from '@hud/web-components/Filters/Filters';
import { ThresholdFilter as ThresholdFilterComponent } from '@hud/web-components/Filters/ThresholdFilter';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import useFilePathsAutocompleteQuery from '../../hooks/useFilePathsAutocompleteQuery';
import useEnvironment from '../../hooks/useEnvironment';
import { ClearAllButton, FiltersIcon, FiltersMenuButton } from './FunctionsTable.styles';
import renderTrendHeader from '../trendHeaderRenderer/trendHeaderRenderer';
import _ from 'lodash';
import tooltips from '../../assets/tooltips';
import { Runtime, ThresholdFilter, TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import { RuntimeIcon } from '@hud/web-components/Runtime/RuntimeIcon';
import { RuntimeLabel } from '@hud/web-components/Runtime/RuntimeLabel';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import renderFunctionName from '../FunctionNameCellRenderer/FunctionNameCellRenderer';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import ServicesFilter from '../ServicesFilter/ServicesFilter';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import P90ColumnHeader from '../P90ColumnHeader/P90ColumnHeader';
import FlowDeploymentsFilter from '../FlowDeploymentsFilter/FlowDeploymentsFilter';
import { FlowDeployments } from '@hud/portal-dtos/webapp-endpoint.dto';
import {
  DEFAULT_DEBOUNCE_TIME,
  DEFAULT_PAGE_SIZE,
  DURATION_AVG_DEFAULT,
  NORMALIZED_RUNS_DEFAULT,
  ERROR_RATE_DEFAULT,
  DURATION_AVG_PRESETS,
  NORMALIZED_RUNS_PRESETS,
  ERROR_RATE_PRESETS,
} from '../../consts';
import p90RenderFunctionMetric from '../functionMetricCellRenderer/p90MetricCellRenderer';
import renderRunsMetric from '../metricCellRenderer/runsCellRenderer';

const PathCell = ({ getValue }: CellContext<FunctionData, string>) => (
  <LongContentPopover reverseEllipsis={true}>{getValue()}</LongContentPopover>
);

export interface FunctionsTableProps {
  aggregatedService?: number;
  aggregatedEndpoint?: number;
  timeFrame: TimeFrame;

  // todo: mixin of table props?
  functions: FunctionData[];
  total?: number;
  filtered?: number;
  loading: boolean;
  title?: string;

  searchValue: string;
  onSearchChange: (searchValue: string) => void;

  sortingState: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;

  showUnnamed: boolean;
  onShowUnnamedChange: (value: boolean) => void;
  filteredFilePaths: string[];
  onFilteredFilePathsChange: (value: string[]) => void;
  filteredServices?: number[];
  onFilteredServicesChange?: (value: number[]) => void;
  flowDeploymentsInTimeframe?: FlowDeployments;
  filteredFlowDeployments?: string[];
  onFilteredFlowDeploymentsChange?: (value: string[]) => void;
  filteredRuntimes?: Runtime[];
  onFilteredRuntimesChange?: (value: Runtime[]) => void;
  filteredDurationAvg?: ThresholdFilter;
  onFilteredDurationAvgChange?: (value: ThresholdFilter | undefined) => void;
  filteredNormalizedRuns?: ThresholdFilter;
  onFilteredNormalizedRunsChange?: (value: ThresholdFilter | undefined) => void;
  filteredErrorRate?: ThresholdFilter;
  onFilteredErrorRateChange?: (value: ThresholdFilter | undefined) => void;
  onClearAllFilters: () => void;

  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;

  referralBreadcrumbs?: BreadcrumbItem[] | undefined;
  tags?: Record<string, string>;

  fullPageTable?: boolean;
}

type FilterType =
  | 'unnamed'
  | 'service'
  | 'filePath'
  | 'runtime'
  | 'deployment'
  | 'durationAvg'
  | 'normalizedRuns'
  | 'errorRate';

interface Filter {
  type: FilterType;
  // Additional properties that each filter might need for its specific configuration
  props?: unknown; // Use more specific types based on actual requirements
}

const columnHelper = createColumnHelper<FunctionData>();

export default function FunctionsTable({
  functions,
  loading,
  aggregatedService,
  aggregatedEndpoint,
  total,
  filtered,
  title,
  searchValue,
  onSearchChange,
  sortingState,
  onSortingChange,
  pages,
  currentPage,
  onPageChange,
  filteredFilePaths,
  onFilteredFilePathsChange,
  filteredServices,
  onFilteredServicesChange,
  flowDeploymentsInTimeframe,
  filteredFlowDeployments,
  onFilteredFlowDeploymentsChange,
  filteredRuntimes,
  onFilteredRuntimesChange,
  filteredDurationAvg,
  onFilteredDurationAvgChange,
  filteredNormalizedRuns,
  onFilteredNormalizedRunsChange,
  filteredErrorRate,
  onFilteredErrorRateChange,
  showUnnamed,
  onShowUnnamedChange,
  onClearAllFilters,
  referralBreadcrumbs,
  tags,
  fullPageTable,
  timeFrame,
}: FunctionsTableProps) {
  const { userPreferredServices } = useUserPreferences();
  const initializeFilters = (): Filter[] => {
    const initialFilters: Filter[] = [];

    if (showUnnamed) {
      initialFilters.push({ type: 'unnamed' });
    }
    if (filteredServices && filteredServices?.length > 0) {
      initialFilters.push({
        type: 'service',
        props: { ids: filteredServices },
      });
    }
    if (filteredFlowDeployments && filteredFlowDeployments?.length > 0) {
      initialFilters.push({
        type: 'deployment',
        props: { deployments: filteredFlowDeployments },
      });
    }
    if (filteredFilePaths.length > 0) {
      initialFilters.push({
        type: 'filePath',
        props: { paths: filteredFilePaths },
      });
    }
    if (filteredRuntimes && filteredRuntimes.length > 0) {
      initialFilters.push({
        type: 'runtime',
        props: { runtimes: filteredRuntimes },
      });
    }
    if (filteredDurationAvg) {
      initialFilters.push({
        type: 'durationAvg',
        props: { durationAvg: filteredDurationAvg },
      });
    }
    if (filteredNormalizedRuns) {
      initialFilters.push({
        type: 'normalizedRuns',
        props: { normalizedRuns: filteredNormalizedRuns },
      });
    }
    if (filteredErrorRate) {
      initialFilters.push({
        type: 'errorRate',
        props: { errorRate: filteredErrorRate },
      });
    }
    return initialFilters;
  };

  const [activeFilters, setActiveFilters] = useState<Filter[]>(initializeFilters);

  useEffect(() => {
    setActiveFilters(initializeFilters());
  }, [filteredServices]);

  const handleFilterChange = (
    filterType: FilterType,
    isActive: boolean,
    filterProps = {},
  ) => {
    if (isActive) {
      if (activeFilters.some((f) => f.type === filterType)) return;
      setActiveFilters((prev) => [
        ...prev.filter((f) => f.type !== filterType),
        { type: filterType, ...filterProps },
      ]);
    } else {
      setActiveFilters((prev) => prev.filter((f) => f.type !== filterType));
    }
  };

  const toggleUnnamedFilter = (isActive: boolean) => {
    handleFilterChange('unnamed', isActive);
    onShowUnnamedChange(isActive);
  };
  const toggleServiceFilter = (isActive: boolean) => {
    handleFilterChange('service', isActive, { ids: filteredServices });
  };
  const toggleFlowDeploymentFilter = (isActive: boolean) => {
    handleFilterChange('deployment', isActive, {
      deployments: filteredFlowDeployments,
    });
  };
  const toggleFilePathFilter = (isActive: boolean) => {
    handleFilterChange('filePath', isActive, { paths: filteredFilePaths });
  };
  const toggleRuntimesFilter = (isActive: boolean) => {
    handleFilterChange('runtime', isActive, { runtimes: filteredRuntimes });
  };
  const toggleDurationAvgFilter = (isActive: boolean) => {
    handleFilterChange('durationAvg', isActive, {
      durationAvg: DURATION_AVG_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'durationAvg')) {
      onFilteredDurationAvgChange?.(DURATION_AVG_DEFAULT);
    }
  };
  const toggleNormalizedRunsFilter = (isActive: boolean) => {
    handleFilterChange('normalizedRuns', isActive, {
      normalizedRuns: NORMALIZED_RUNS_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'normalizedRuns')) {
      onFilteredNormalizedRunsChange?.(NORMALIZED_RUNS_DEFAULT);
    }
  };
  const toggleErrorRateFilter = (isActive: boolean) => {
    handleFilterChange('errorRate', isActive, {
      errorRate: ERROR_RATE_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'errorRate')) {
      onFilteredErrorRateChange?.(ERROR_RATE_DEFAULT);
    }
  };

  const [filePathSearchValue, setFilePathSearchValue] = useInputState('');

  const [debouncedFilePathSearchValue] = useDebouncedValue(
    filePathSearchValue,
    DEFAULT_DEBOUNCE_TIME,
  );

  const { environment } = useEnvironment();

  const filePathAutocompleteQuery = useFilePathsAutocompleteQuery({
    searchTerms: debouncedFilePathSearchValue.split(' ').filter(Boolean),
    serviceId: aggregatedService,
    environmentName: environment?.name,
    selectedPaths: filteredFilePaths,
  });

  useEffect(() => {
    if (!filePathAutocompleteQuery.isSuccess) return;

    const filePaths =
      filePathAutocompleteQuery.data?.map(({ filePath }) => filePath) ?? [];
    const newFilteredFilePaths = filteredFilePaths.filter((path) =>
      filePaths.includes(path),
    );
    if (_.isEqual(newFilteredFilePaths, filteredFilePaths)) return;
    onFilteredFilePathsChange(newFilteredFilePaths);
  }, [
    filteredFilePaths,
    onFilteredFilePathsChange,
    filePathAutocompleteQuery.data,
    filePathAutocompleteQuery.isSuccess,
  ]);

  const columns = useMemo(() => {
    let columns = [
      columnHelper.accessor('signature', {
        header: 'Function',
        cell: renderFunctionName(
          aggregatedEndpoint,
          aggregatedService,
          referralBreadcrumbs,
          tags,
        ),
        meta: {
          copyable: true,
          disableAutoFit: true,
          copyableItems: [
            {
              label: 'signature',
              accessor: (row: FunctionData) => row.signature,
              icon: 'other.codeBrackets',
            },
            {
              label: 'path',
              accessor: (row: FunctionData) => row.filePath,
              leftSection: '/...',
            },
          ],
          description: tooltips.FUNCTIONS_TABLE.FUNCTION,
        },
        minSize: 120,
        maxSize: 800,
        size: 250,
      }),
      columnHelper.accessor('metrics.runs.value', {
        id: 'runs.value',
        header: 'Runs (Runs/Day)',
        cell: renderRunsMetric(timeFrame),
        minSize: timeFrame === 'hour' ? 150 : 220,
        maxSize: 300,
        meta: {
          fitFormatter: formatFunctionMetric('number'),
          description: tooltips.FUNCTIONS_TABLE.RUNS,
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.runs', {
        id: 'runs.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 55,
        minSize: 55,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.RUNS_TREND,
        },
      }),
      columnHelper.accessor('metrics.durationAvg.value', {
        id: 'durationAvg.value',
        header: 'Dur avg',
        cell: renderFunctionMetric('duration'),
        size: 120,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.DURATION,
        },
      }),
      columnHelper.accessor('metrics.durationAvg', {
        id: 'durationAvg.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.DURATION_TREND,
        },
      }),
      columnHelper.accessor('metrics.durationP90.value', {
        id: 'durationP90.value',
        header: P90ColumnHeader,
        cell: p90RenderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 130,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.durationP90', {
        id: 'durationP90.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.DURATION_TREND,
        },
      }),
      columnHelper.accessor('metrics.errorRate.value', {
        id: 'errorRate.value',
        header: 'Err rate',
        cell: renderFunctionMetric('percent'),
        size: 120,
        minSize: 100,
        maxSize: 200,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.ERRORS,
        },
      }),
      columnHelper.accessor('metrics.errorRate', {
        id: 'errorRate.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.ERRORS_TREND,
        },
      }),
      columnHelper.accessor('lastInvokedAt', {
        header: 'Last invoked',
        cell: renderDatetime(true),
        minSize: 80,
        size: 160,
        maxSize: 200,
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.LAST_INVOKED,
        },
      }),
      columnHelper.accessor('lastChangeAt', {
        header: 'Last changed',
        cell: renderDatetime(true),
        minSize: 60,
        size: 170,
        maxSize: 200,
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.LAST_CHANGED,
        },
      }),
      columnHelper.accessor('firstSeenAt', {
        header: 'First mapped',
        cell: renderDatetime(true),
        minSize: 60,
        size: 160,
        maxSize: 200,
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
          description: tooltips.FUNCTIONS_TABLE.FIRST_MAPPED,
        },
      }),
      columnHelper.accessor('filePath', {
        header: 'Path',
        cell: PathCell,
        minSize: 100,
        maxSize: 600,
        meta: {
          copyable: true,
          disableAutoFit: true,
          description: tooltips.FUNCTIONS_TABLE.FILE_PATH,
        },
      }),
      columnHelper.accessor('totalServices', {
        header: 'Srvcs',
        cell: entitiesCellRenderer('services'),
        minSize: 80,
        size: 110,
        maxSize: 200,
        meta: {
          fitFormatter: entitiesCellFormatter('services'),
          disableAutoFit: true,
          description: tooltips.FUNCTIONS_TABLE.SERVICES,
        },
      }),
      columnHelper.accessor('runtime', {
        header: 'Language',
        cell: (cell) => <RuntimeIcon runtime={cell.getValue()} />,
        minSize: 80,
        maxSize: 120,
        meta: {
          description: tooltips.FUNCTIONS_TABLE.LANGUAGE,
        },
      }),
    ];
    if (aggregatedService) {
      columns = columns.filter(
        (column) =>
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - bug - accessorKey is not defined in ColumnDefBase somehow
          column.accessorKey !== 'totalServices' &&
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - bug - accessorKey is not defined in ColumnDefBase somehow
          column.accessorKey !== 'runtime',
      );
    }
    if (aggregatedEndpoint) {
      columns = columns.filter(
        (column) =>
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - bug - accessorKey is not defined in ColumnDefBase somehow
          column.accessorKey !== 'totalServices',
      );
    }
    return columns;
  }, [aggregatedService, aggregatedEndpoint, referralBreadcrumbs, timeFrame]);

  const table = useReactTable({
    data: functions,
    columns,
    state: {
      sorting: sortingState,
    },

    defaultColumn: {
      minSize: 50,
    },
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onSortingChange,
    enableSortingRemoval: false,

    columnResizeMode: 'onChange',
  });

  const hasFilters = activeFilters.length > 0;

  let subsetCounter = null;
  if (title) {
    subsetCounter = loading ? (
      <Skeleton width={100} />
    ) : (
      <ItemCounter total={total || 0} filtered={filtered || 0} />
    );
  }

  return (
    <TableContainer.Container
      title={loading ? <Skeleton width={140} /> : title || `Showing ${filtered}/${total}`}
    >
      <TableContainer.Header>
        <TableContainer.Filters>
          <Search inline value={searchValue} onSearchChange={onSearchChange} />
          {activeFilters.map((filter) => {
            switch (filter.type) {
              case 'unnamed':
                return (
                  <FlagFilter
                    key={filter.type}
                    label="Unnamed function are shown"
                    onClear={() => toggleUnnamedFilter(false)}
                  />
                );
              case 'service':
                return (
                  <ServicesFilter
                    key={filter.type}
                    userPreferredServices={userPreferredServices ?? []}
                    filteredServices={filteredServices}
                    onFilteredServicesChange={onFilteredServicesChange}
                    handleFilterChange={handleFilterChange}
                  />
                );
              case 'filePath':
                return (
                  <MultiSelectFilter
                    key={filter.type}
                    label="File paths"
                    options={
                      filePathAutocompleteQuery.data?.map(({ filePath }) => ({
                        value: filePath,
                        label: filePath,
                      })) ?? []
                    }
                    optionReverseEllipsis
                    onClear={() => {
                      onFilteredFilePathsChange([]);
                      toggleFilePathFilter(false);
                    }}
                    onChange={onFilteredFilePathsChange}
                    onSearch={setFilePathSearchValue}
                    searchTerm={filePathSearchValue}
                    onClose={() => setFilePathSearchValue('')}
                    multiSelectLabel={
                      filteredFilePaths.length > 0
                        ? filteredFilePaths.length === 1
                          ? filteredFilePaths[0]
                          : `${filteredFilePaths.length} filtered
                          `
                        : 'Select…'
                    }
                    selectedOptions={filteredFilePaths}
                  />
                );
              case 'runtime':
                return (
                  <MultiSelectFilter
                    key={filter.type}
                    label="Languages"
                    options={[
                      {
                        value: 'nodejs',
                        label: <RuntimeLabel runtime="nodejs" />,
                      },
                      {
                        value: 'python',
                        label: <RuntimeLabel runtime="python" />,
                      },
                    ]}
                    onClear={() => {
                      onFilteredRuntimesChange?.([]);
                      toggleRuntimesFilter(false);
                    }}
                    onChange={(values: string[]) => {
                      onFilteredRuntimesChange?.(values as Runtime[]);
                    }}
                    multiSelectLabel={
                      filteredRuntimes && filteredRuntimes.length > 0 ? (
                        filteredRuntimes.length === 1 ? (
                          <RuntimeLabel runtime={filteredRuntimes[0]} />
                        ) : (
                          `${filteredRuntimes.length} filtered`
                        )
                      ) : (
                        'Select…'
                      )
                    }
                    selectedOptions={filteredRuntimes ?? []}
                  />
                );
              case 'deployment':
                return (
                  <FlowDeploymentsFilter
                    key={filter.type}
                    deployments={flowDeploymentsInTimeframe ?? []}
                    filteredFlowDeployments={filteredFlowDeployments}
                    onFilteredFlowDeploymentsChange={onFilteredFlowDeploymentsChange}
                    handleFilterChange={handleFilterChange}
                  />
                );
              case 'durationAvg':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Duration Avg"
                    value={filteredDurationAvg?.value ?? 0}
                    operator={filteredDurationAvg?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredDurationAvgChange?.({
                        ...(filteredDurationAvg as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredDurationAvgChange?.({
                        ...(filteredDurationAvg as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={DURATION_AVG_PRESETS}
                    unit="ms"
                    onClear={() => {
                      onFilteredDurationAvgChange?.(undefined);
                      toggleDurationAvgFilter(false);
                    }}
                  />
                );
              case 'normalizedRuns':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Runs/d"
                    value={filteredNormalizedRuns?.value ?? 0}
                    operator={filteredNormalizedRuns?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredNormalizedRunsChange?.({
                        ...(filteredNormalizedRuns as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredNormalizedRunsChange?.({
                        ...(filteredNormalizedRuns as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={NORMALIZED_RUNS_PRESETS}
                    onClear={() => {
                      onFilteredNormalizedRunsChange?.(undefined);
                      toggleNormalizedRunsFilter(false);
                    }}
                  />
                );
              case 'errorRate':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Failing"
                    value={filteredErrorRate?.value ?? 0}
                    operator={filteredErrorRate?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredErrorRateChange?.({
                        ...(filteredErrorRate as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredErrorRateChange?.({
                        ...(filteredErrorRate as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={ERROR_RATE_PRESETS}
                    unit="%"
                    onClear={() => {
                      onFilteredErrorRateChange?.(undefined);
                      toggleErrorRateFilter(false);
                    }}
                  />
                );
              default:
                return null;
            }
          })}

          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              {aggregatedService ? null : (
                <Menu.Item onClick={() => toggleServiceFilter(true)}>Services</Menu.Item>
              )}

              <Menu.Item onClick={() => toggleFilePathFilter(true)}>File paths</Menu.Item>
              {aggregatedService ? null : (
                <Menu.Item onClick={() => toggleRuntimesFilter(true)}>
                  Languages
                </Menu.Item>
              )}
              {aggregatedEndpoint ? (
                <Menu.Item onClick={() => toggleFlowDeploymentFilter(true)}>
                  Deployed at
                </Menu.Item>
              ) : null}
              <Menu.Divider />
              <Menu.Item onClick={() => toggleNormalizedRunsFilter(true)}>
                Invoked often
              </Menu.Item>
              <Menu.Item onClick={() => toggleDurationAvgFilter(true)}>
                Exceeding duration
              </Menu.Item>
              <Menu.Item onClick={() => toggleErrorRateFilter(true)}>Failing</Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={() => toggleUnnamedFilter(true)}>
                Show unnamed functions
              </Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>
          {hasFilters ? (
            <ClearAllButton
              onClick={() => {
                toggleUnnamedFilter(false);
                toggleServiceFilter(false);
                toggleFilePathFilter(false);
                toggleFlowDeploymentFilter(false);
                toggleRuntimesFilter(false);
                toggleDurationAvgFilter(false);
                toggleNormalizedRunsFilter(false);
                toggleErrorRateFilter(false);

                onClearAllFilters();
              }}
            >
              Clear All
            </ClearAllButton>
          ) : null}
          {subsetCounter}
        </TableContainer.Filters>
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Functions Table"
          loading={loading}
          loadingRows={DEFAULT_PAGE_SIZE}
          options={{
            emptyStateProps: {
              expanded: fullPageTable,
            },
          }}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={pages}
          currentPage={currentPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
